import { Inline, Cover } from '@bedrock-layout/primitives'
import BrandSearchInput from '@okam/brand-ui/components/fields/BrandSearchInput'
import { FilterReactAria, Heading, Img, Paragraph, TeamMemberCard } from '@okam/brand-ui/index'
import { extractRelativePath } from '@okam/html2react/index'
import { Icon } from '@okam/shared-ui'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import type { Key } from 'react'
import { useState } from 'react'
import { Item, Section } from 'react-stately'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import tw from 'twin.macro'
import { MainLayout } from '@/components/layout'
import { useFragment } from 'gql'
import type {
  AllEquipeQuery,
  AllMemberTypesQuery,
  EquipeQuery,
  ExpertiseTypeQuery,
  MainMenuFragment,
  SiteOptionsQuery,
} from 'gql/graphql'
import { allMembersFragment } from 'query/fragments'
import { TEAM_LISTING_CARD_HEIGHT, TEAM_LISTING_CARD_WIDTH } from '../../../../const'

export interface TEquipePageProps {
  pageProps: EquipeQuery & AllEquipeQuery & SiteOptionsQuery & AllMemberTypesQuery & ExpertiseTypeQuery
}

const EquipePage = (props: TEquipePageProps) => {
  const { pageProps } = props
  const { siteOptions, page, members, mainMenu } = pageProps

  const allMembers = useFragment(allMembersFragment, pageProps.members)
  const [selectedValue, setSelectedValue] = useState<string | number>()
  const [filtered, setFiltered] = useState<boolean>(false)
  const membersTypes = pageProps?.memberTypes?.nodes ?? []
  const { t } = useTranslation('common')
  const router = useRouter()
  const { query } = router
  const queryExpertise = query?.expertise as string

  const filterAriaLabel = t('ARIA.FILTER')
  const filterPlaceholder = t('FILTER.BY')
  const filterLabel = t('FILTER.MEMBERS')
  const filterTypesLabel = t('FILTER.SELECT_FIRST_SECTION_LABEL_EQUIPE')
  const noResult = t('FILTER.NO_RESULT')

  const expertiseMembers =
    queryExpertise && members
      ? members.nodes.filter(
          (member) =>
            member &&
            member.memberFields &&
            member.memberFields.memberExpertises &&
            member.memberFields.memberExpertises.some((expertise) => expertise && expertise.slug === queryExpertise),
        )
      : members?.nodes

  const filteredMembers =
    members &&
    members?.nodes.filter((member) => {
      const hasSelectedValue = member?.memberTypes?.nodes?.some((type) => type?.slug === selectedValue)
      return !selectedValue || hasSelectedValue
    })

  const membersList = filtered ? filteredMembers : expertiseMembers

  const membersData = members?.nodes?.map((member) => ({
    id: member.id,
    title: member.title,
    url: member.link,
  }))

  return (
    <MainLayout
      siteOptionsProps={siteOptions}
      seo={page?.seo}
      menuItems={mainMenu as MainMenuFragment}
      allMembers={allMembers}
      variant="colored"
      translatedUri={page?.translatedUri}
    >
      <Cover tw="bg-tertiary-1" stretchContent>
        <Inline
          justify="start"
          tw="flex flex-col h-full pt-24 pb-10 lg:pt-28 lg:pb-16 xl:pt-32 px-6 mx-auto md:px-12 lg:px-14 w-full max-w-[1920px]"
        >
          <Heading as="h1" variant="h1" tw="uppercase font-semibold text-tertiary-5 mt-32 mb-8 md:mb-12">
            {page?.title ?? ''}
          </Heading>
          <div tw="flex flex-wrap gap-6 items-start lg:items-end justify-between mb-8 w-full z-10">
            <div tw="basis-full w-full md:basis-1/4">
              <FilterReactAria
                id="team-filter"
                label={filterLabel}
                name="team-filter"
                placeholderLabel={filterPlaceholder}
                ariaLabel={filterAriaLabel}
                onSelectionChange={(value: Key) => {
                  setSelectedValue(value)
                  setFiltered(true)
                }}
                selectedKey={selectedValue}
                popoverStyles={tw`w-[calc(100vw - 3rem)] md:w-[calc(100vw - 6rem)] lg:w-auto`}
              >
                <Section title={filterTypesLabel}>
                  {membersTypes &&
                    membersTypes.map((type) => (
                      <Item textValue={type.name ?? ''} key={type.slug}>
                        <Paragraph variant="p" as="p">
                          {type.name}
                        </Paragraph>
                      </Item>
                    ))}
                </Section>
              </FilterReactAria>
            </div>
            <div tw="basis-full md:basis-auto">
              <BrandSearchInput
                id="search-team"
                name="search-team"
                placeholder={t('PLACEHOLDER.SEARCH') as string}
                aria-label={t('ARIA.SEARCH_MEMBER')}
                dataFuzzySearch={membersData}
                tw="w-full bg-tertiary-1 min-w-[20rem]"
                icon={<Icon icon="Search" tw="!text-tertiary-3" />}
              />
            </div>
          </div>
          {membersList && membersList.length ? (
            <div tw="w-full items-start grid grid-cols-2 gap-6 lg:grid-cols-4">
              {membersList
                ?.sort((a, b) => {
                  const aLastName = a.memberFields?.memberLastname ?? ''
                  const bLastName = b.memberFields?.memberLastname ?? ''

                  if (!aLastName && !bLastName) {
                    return -1
                  }

                  if (!aLastName && bLastName) {
                    return 1
                  }

                  if (aLastName && !bLastName) {
                    return -1
                  }

                  return aLastName.localeCompare(bLastName)
                })
                ?.map((member, index: number) => (
                  <TeamMemberCard
                    // eslint-disable-next-line react/no-array-index-key
                    key={`team-member-card-${index}-${member.title}`}
                    permalink={member.link ? extractRelativePath(member.link) : ''}
                    email={member.memberFields?.memberEmail ?? ''}
                    name={member.title ?? ''}
                    phone={member.memberFields?.memberPhoneNumber ?? ''}
                  >
                    <div style={{ height: '100%' }}>
                      {member.featuredImage ? (
                        <Img
                          src={member.featuredImage?.node.sourceUrl ?? ''}
                          width={TEAM_LISTING_CARD_WIDTH}
                          height={TEAM_LISTING_CARD_HEIGHT}
                          sizes="(max-width: 768px) 50vw, 600px"
                          alt={member.title ?? ''}
                        />
                      ) : (
                        <div tw="bg-tertiary-2 h-full" />
                      )}
                    </div>
                  </TeamMemberCard>
                ))}
            </div>
          ) : (
            <div tw="relative">
              <Paragraph as="p" variant="p" tw="pt-4">
                {noResult}
              </Paragraph>
            </div>
          )}
        </Inline>
      </Cover>
    </MainLayout>
  )
}

export default EquipePage
